import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFilm,
  faTicketAlt,
  faArrowRight,
  faCrown,
  faExclamationTriangle,
  faCheckCircle,
  faPlus,
  faClock,
  faUsers,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';

import filmTourImage from '../../../assets/tours/film-lovers.webp';
import castleImage from '../../../assets/castle2.png';

const BookingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const ImportantNotice = styled.div`
  background: linear-gradient(135deg, #ff6b35, #f7931e);
  color: white;
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 3rem;
  text-align: center;
  box-shadow: 0 4px 20px rgba(255, 107, 53, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(255,255,255,0.1)"/></svg>') repeat;
    background-size: 20px 20px;
  }
  
  h2 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    position: relative;
    z-index: 1;
  }
  
  p {
    font-size: 1.2rem;
    line-height: 1.6;
    position: relative;
    z-index: 1;
    
    &:last-child {
      margin-top: 1rem;
      font-weight: 600;
      font-size: 1.3rem;
    }
  }
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 2rem;
  margin: 3rem 0;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const StepCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  text-align: center;
  border: 3px solid ${props => props.primary ? props.theme.colors.accent : '#e0e0e0'};
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
  
  p {
    color: ${props => props.theme.colors.secondary};
    line-height: 1.6;
  }
`;

const PlusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: ${props => props.theme.colors.accent};
  color: white;
  border-radius: 50%;
  font-size: 2rem;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    transform: rotate(90deg);
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
    margin: 1rem auto 0;
  }
`;

const TourDetails = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 3rem;
  
  h3 {
    font-size: 1.8rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.6;
    color: ${props => props.theme.colors.secondary};
    margin-bottom: 1.5rem;
  }
`;

const TourTypeBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: 0.5rem;
`;

const AddOnBadge = styled(TourTypeBadge)`
  background-color: ${props => props.theme.colors.accent};
  color: white;
`;

const InclusiveBadge = styled(TourTypeBadge)`
  background-color: #28a745;
  color: white;
`;

const TourDateCard = styled.div`
  background: ${props => props.inclusive ? '#e8f5e8' : '#f8f9fa'};
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  border: 2px solid ${props => props.inclusive ? '#28a745' : '#e0e0e0'};
  position: relative;
  
  strong {
    color: ${props => props.inclusive ? '#28a745' : props.theme.colors.primary};
  }
`;

const TourTypeSection = styled.div`
  margin-bottom: 2rem;
  
  h4 {
    font-size: 1.3rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const TourMeta = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  color: ${props => props.theme.colors.secondary};
  font-size: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  justify-content: center;
  margin-top: 3rem;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin: 3rem auto 0;
  }
`;

const OptionCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  max-width: 500px;
  width: 100%;
  
  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const OptionImage = styled.div`
  height: 200px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
`;

const OptionContent = styled.div`
  padding: 2rem;
  text-align: center;
`;

const OptionTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.6rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const OptionDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1.5rem;
`;

const BookButton = styled.a`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-weight: 600;
  border: 2px solid ${props => props.theme.colors.accent};
  transition: all 0.3s ease;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
  }
  
  svg {
    margin-left: 0.5rem;
  }
`;

const UpsellNotice = styled.div`
  background: #f8f9fa;
  border: 2px dashed ${props => props.theme.colors.accent};
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
  text-align: center;
  
  p {
    margin: 0;
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
    
    svg {
      color: ${props => props.theme.colors.accent};
      margin-right: 0.5rem;
    }
  }
`;

const FilmLoversTour = () => {
  return (
    <BookingContainer>
      <ParallaxBanner 
        title="Film Lover's Tour"
        subtitle="Step into the State Rooms Featured in The Crown & More"
        image={filmTourImage}
      />
      
      <ContentContainer>
        <SectionTitle>Choose Your Booking Option</SectionTitle>
        
        <OptionsGrid>
          <OptionCard>
            <OptionImage image={castleImage} />
            <OptionContent>
              <OptionTitle>
                <FontAwesomeIcon icon={faCrown} />
                Castle & Gardens Entry + Film Tour Add-On
              </OptionTitle>
              <OptionDescription>
                Full access to the Castle State Rooms and Gardens. Add the Film Lover's Tour 
                for just £5 extra during checkout. Available for most tour dates.
              </OptionDescription>
              <BookButton href="https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=9ccb592f-b03f-4762-9b48-5f7017dcced5" target="_blank" rel="noopener noreferrer">
                Book Castle Entry & Add Film Tour <FontAwesomeIcon icon={faArrowRight} />
              </BookButton>
              <UpsellNotice>
                <p>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Film Lover's Tour (£5pp) available as add-on during booking
                </p>
              </UpsellNotice>
            </OptionContent>
          </OptionCard>

          <OptionCard>
            <OptionImage image={filmTourImage} />
            <OptionContent>
              <OptionTitle>
                <FontAwesomeIcon icon={faFilm} />
                All-Inclusive Film Tour - August 30th
              </OptionTitle>
              <OptionDescription>
                <strong>Special Offer:</strong> This tour includes both Castle & Gardens entry AND the Film Lover's Tour 
                in one ticket - no additional costs! Perfect for those who want everything included upfront.
              </OptionDescription>
              <BookButton href="https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=018a2bca-550c-467a-9576-629ea620095b" target="_blank" rel="noopener noreferrer">
                Book All-Inclusive Tour <FontAwesomeIcon icon={faArrowRight} />
              </BookButton>
              <UpsellNotice>
                <p>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Includes Castle Entry + Film Tour in one ticket
                </p>
              </UpsellNotice>
            </OptionContent>
          </OptionCard>
        </OptionsGrid>

        <TourDetails style={{ marginTop: '4rem' }}>
          <h3>
            <FontAwesomeIcon icon={faCalendarAlt} />
            Available Tour Dates
          </h3>
          
          <TourTypeSection>
            <h4>
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#28a745' }} />
              All-Inclusive Film Tour (No Extra Cost)
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              <TourDateCard inclusive>
                <strong>30th August</strong><br />
                2:00 PM
                <InclusiveBadge>All-Inclusive</InclusiveBadge>
              </TourDateCard>
            </div>
          </TourTypeSection>

          <TourTypeSection>
            <h4>
              <FontAwesomeIcon icon={faPlus} style={{ color: props => props.theme.colors.accent }} />
              Add-On Film Tours (£5 Extra)
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem', fontStyle: 'italic' }}>
              These tours require a separate Castle & Gardens entry ticket, with the Film Tour as an add-on option.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <TourDateCard>
                <strong>24th July</strong><br />
                2:00 PM
                <AddOnBadge>Add-On</AddOnBadge>
              </TourDateCard>
              <TourDateCard>
                <strong>30th July</strong><br />
                11:00 AM
                <AddOnBadge>Add-On</AddOnBadge>
              </TourDateCard>
              <TourDateCard>
                <strong>7th August</strong><br />
                2:00 PM
                <AddOnBadge>Add-On</AddOnBadge>
              </TourDateCard>
              <TourDateCard>
                <strong>13th August</strong><br />
                11:00 AM
                <AddOnBadge>Add-On</AddOnBadge>
              </TourDateCard>
              <TourDateCard>
                <strong>21st August</strong><br />
                2:00 PM
                <AddOnBadge>Add-On</AddOnBadge>
              </TourDateCard>
              <TourDateCard>
                <strong>27th August</strong><br />
                11:00 AM
                <AddOnBadge>Add-On</AddOnBadge>
              </TourDateCard>
              <TourDateCard>
                <strong>2nd October</strong><br />
                2:00 PM
                <AddOnBadge>Add-On</AddOnBadge>
              </TourDateCard>
              <TourDateCard>
                <strong>9th October</strong><br />
                2:00 PM
                <AddOnBadge>Add-On</AddOnBadge>
              </TourDateCard>
              <TourDateCard>
                <strong>16th October</strong><br />
                2:00 PM
                <AddOnBadge>Add-On</AddOnBadge>
              </TourDateCard>
              <TourDateCard>
                <strong>23rd October</strong><br />
                2:00 PM
                <AddOnBadge>Add-On</AddOnBadge>
              </TourDateCard>
              <TourDateCard>
                <strong>29th October</strong><br />
                2:00 PM
                <AddOnBadge>Add-On</AddOnBadge>
              </TourDateCard>
            </div>
          </TourTypeSection>
        </TourDetails>

        <TourDetails>
          <h3>
            <FontAwesomeIcon icon={faFilm} />
            About the Film Lover's Tour
          </h3>
          <p>
            Join our knowledgeable guides and step into the State Rooms that were used as locations 
            in the popular Netflix Series, The Crown and films such as The Young Victoria and Victoria & Abdul. 
            Hear firsthand about the film crews using our beautiful Castle and Grounds in location filming.
          </p>
          <TourMeta>
            <div>
              <FontAwesomeIcon icon={faClock} /> Duration: 1½ hours
            </div>
            <div>
              <FontAwesomeIcon icon={faUsers} /> Small groups for better experience
            </div>
          </TourMeta>
        </TourDetails>
      </ContentContainer>
      
      <Footer />
    </BookingContainer>
  );
};

export default FilmLoversTour; 